import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Eye, Loader2, Repeat, ThumbsUp } from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { formatDisplayDate } from "@/lib/dateFormat";
import { dashboardKeys, invoiceKeys } from "@/lib/queryKeys";
import {
  confirmRecurringOccurrence,
  listRecurringSchedules,
} from "@/services/recurringInvoiceService";
import type { RecurringPendingDraftItem } from "@fiscalo/shared-types";

interface DashboardRecurringInvoicesCardProps {
  schedulesActiveCount: number;
  pendingDrafts: RecurringPendingDraftItem[];
}

function formatIsoDdMmYyyy(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function formatAmount(amount: number, currency: "UYU" | "USD"): string {
  const formatted = amount.toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${currency} ${formatted}`;
}

/**
 * Recurring-invoices summary, ported from the mobile Inicio card: leads with
 * the next scheduled client ("Próxima: HANDY") and its send date rather than a
 * bare "N facturas recurrentes configuradas" count.
 *
 * Both dashboard views render this identically — the two card languages had
 * diverged, and the same information should not look like two different
 * features depending on a display preference.
 *
 * The pending-draft rows keep their Vista previa / Aprobar actions: unifying
 * the styling must not cost the advanced view the ability to approve a draft
 * from the dashboard.
 *
 * The schedule list is not in the dashboard payload — only the active count is
 * — so this fetches it. While loading, or if the request fails, it falls back
 * to the count, so the card is never empty.
 */
export function DashboardRecurringInvoicesCard({
  schedulesActiveCount,
  pendingDrafts,
}: DashboardRecurringInvoicesCardProps): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: schedules } = useQuery({
    queryKey: ["recurring-schedules", "dashboard-summary"],
    queryFn: () => listRecurringSchedules({ limit: 50 }),
    staleTime: 60_000,
    retry: false,
  });

  const next = useMemo(() => {
    const active = (schedules?.data ?? []).filter((s) => s.isActive);
    if (active.length === 0) return null;
    return active
      .slice()
      .sort((a, b) => a.nextSendDate.localeCompare(b.nextSendDate))[0];
  }, [schedules]);

  const approveMut = useMutation({
    mutationFn: (occurrenceId: string) =>
      confirmRecurringOccurrence(occurrenceId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: dashboardKeys.metrics() }),
        queryClient.invalidateQueries({ queryKey: invoiceKeys.all }),
      ]);
      toast.success(t("dashboard.recurringCard.approvedToast"));
    },
    onError: () => {
      toast.error(t("dashboard.recurringCard.approveError"));
    },
  });

  if (schedulesActiveCount === 0 && pendingDrafts.length === 0) return null;

  const clientName =
    next?.client?.displayName?.trim() ||
    next?.client?.legalName?.trim() ||
    t("dashboard.recurringCard.fallbackClient", "Cliente");

  return (
    <section>
      <div className="fx-section-head">
        <span className="fx-eyebrow">{t("dashboard.recurringCard.title")}</span>
        <Link to="/invoices/recurring" className="fx-section-head-link">
          {t("dashboard.activity.viewAll", "Ver todas →")}
        </Link>
      </div>

      <div className="space-y-2">
        <Link to="/invoices/recurring" className="fx-recurring-simple">
          <span className="fx-recurring-simple-ico">
            <Repeat aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="fx-recurring-simple-title block truncate">
              {next
                ? t("dashboard.recurringCard.nextUp", "Próxima: {{client}}", {
                    client: clientName,
                  })
                : t("dashboard.recurringCard.summary", {
                    count: schedulesActiveCount,
                  })}
            </span>
            {next ? (
              <span className="fx-recurring-simple-sub block">
                {formatDisplayDate(next.nextSendDate, {
                  locale: i18n.language,
                  useCalendarDate: true,
                })}
                {schedulesActiveCount > 1
                  ? ` · ${t("dashboard.recurringCard.summary", { count: schedulesActiveCount })}`
                  : ""}
              </span>
            ) : null}
          </span>
        </Link>

        {pendingDrafts.map((d) => {
          const isApproving =
            approveMut.isPending && approveMut.variables === d.occurrenceId;
          return (
            <div
              key={d.occurrenceId}
              className="flex flex-col gap-2 rounded-[14px] border border-[var(--fx-ink-100)] border-l-4 border-l-[var(--fx-ink-900)] bg-white p-3 text-sm dark:border-[var(--dk-border-2)] dark:border-l-[var(--fx-paper)] dark:bg-[var(--dk-surface)]"
            >
              <div className="min-w-0">
                <p className="truncate font-semibold text-foreground">
                  {d.clientName}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {t("dashboard.recurringCard.draftMeta", {
                    date: formatIsoDdMmYyyy(d.sendDate),
                    amount: formatAmount(d.amount, d.currency),
                  })}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/invoices/${d.invoiceId}`)}
                  disabled={!d.invoiceId}
                >
                  <Eye className="mr-1.5 h-3.5 w-3.5" />
                  {t("dashboard.recurringCard.preview")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => approveMut.mutate(d.occurrenceId)}
                  disabled={isApproving || approveMut.isPending}
                >
                  {isApproving ? (
                    <>
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      {t("dashboard.recurringCard.approving")}
                    </>
                  ) : (
                    <>
                      <ThumbsUp className="mr-1.5 h-3.5 w-3.5" />
                      {t("dashboard.recurringCard.approve")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
